import { useEffect, useId, useRef, useState } from 'react';

interface Props {
  /** Button text in its resting state. */
  label: string;
  /** What happens if they go ahead — one or two plain sentences. */
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  disabled?: boolean;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}

/** A two-step button: press once to see the consequences, again to do it. Keyboard-usable, no modal. */
export function ConfirmAction({ label, message, confirmLabel, danger, disabled, pending, onConfirm }: Props) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);
  if (!open) {
    return (
      <button
        type="button"
        className={`btn ${danger ? 'btn-danger' : ''}`}
        disabled={disabled || pending}
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-controls={id}
      >
        {label}
      </button>
    );
  }
  return (
    <fieldset className="confirm" id={id}>
      <legend className="visually-hidden">{label}</legend>
      <p>{message}</p>
      <div className="btn-row">
        <button
          type="button"
          className={`btn ${danger ? 'btn-danger btn-solid' : 'btn-primary'}`}
          disabled={pending}
          onClick={async () => {
            await onConfirm();
            setOpen(false);
          }}
        >
          {pending ? 'Working…' : (confirmLabel ?? label)}
        </button>
        <button
          type="button"
          className="btn"
          disabled={pending}
          onClick={() => setOpen(false)}
          ref={cancelRef}
        >
          Cancel
        </button>
      </div>
    </fieldset>
  );
}
